"use client";

import { queryKeys } from "@/constants/queries.keys";
import { searchPackagesList } from "@/services/packages/searchPackages";
import type { SearchPackagesListReturn } from "@/types/packages/data-only/SearchPackagesList";
import { useQuery } from "@tanstack/react-query";
import { get, set } from "idb-keyval";
import { useLocale } from "next-intl";

const oneDay = 1000 * 60 * 60 * 24;

type CacheEntry = {
  data: SearchPackagesListReturn;
  fetchedAt: number;
};

export function useSearchPackagesList(enabled = true) {
  const locale = useLocale();

  return useQuery({
    queryKey: [queryKeys.packages.searchPackagesList, locale],
    queryFn: async () => {
      const cached = await get<CacheEntry>(`search-packages:${locale}`);
      const isFresh = !!cached?.data && Date.now() - cached.fetchedAt < oneDay;

      if (isFresh) {
        return cached.data;
      }

      const res = await searchPackagesList({ locale });
      if (!res.status) {
        throw new Error(res.message);
      }

      await set(`search-packages:${locale}`, {
        data: res.data,
        fetchedAt: Date.now(),
      });

      return res.data;
    },
    enabled,
    staleTime: Infinity,
  });
}
